import { graphql, Link, useStaticQuery } from 'gatsby'
import React, { useEffect, useMemo } from 'react'
import { routePropTypes } from '../../utils/routes'
import { useLeftNavAccordion } from '../../hooks/useLeftNavAccordion'
import { getNavigationNodes, isCortexPath } from '../../utils/cortexNavigation'

import './_tableofcontents.less'
import NavigationNode from '../NavigationNode/NavigationNode'

/** Pages we want to ignore from the root level navigation */
const EXCLUSION_PATHS = ['/faq/', '/cla/', '/style-comp/']
const TOP_LEVEL_NAV_SPLIT_LENGTH = 3

// TODO: turn this into a function that can get the first child of the respective key
const REDIRECT_MAP = new Map()
REDIRECT_MAP.set('/developers/', '/developers/credential-management')
REDIRECT_MAP.set('/guides/', '/guides/getting-started/create-an-input')

const TableOfContents = ({ metadata }) => {
    const data = useStaticQuery(graphql`
        query {
            allPageMetadata {
                edges {
                    node {
                        title
                        path
                        redirect
                        order
                        hideFromNav
                    }
                }
            }
        }
    `)

    // Scroll to parent section and ensure active child is visible
    useEffect(() => {
        const parentElement = document.querySelector('.header-section__parent--selected')
        const activeElement = document.getElementById('header-section--active')

        if (parentElement) {
            // First, scroll to show the parent section
            parentElement.scrollIntoView({
                behavior: 'instant',
                block: 'start',
            })

            if (activeElement) {
                setTimeout(() => {
                    const rect = activeElement.getBoundingClientRect()
                    const container = document.querySelector('.left-nav-pane')
                    const containerRect = container.getBoundingClientRect()

                    if (rect.bottom > containerRect.bottom) {
                        activeElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'end',
                        })
                    }
                    // Check if active element is above the visible area
                    else if (rect.top < containerRect.top) {
                        activeElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        })
                    }
                }, 50)
            }

            // Ensure the document itself is not scrolled
            document.documentElement.scrollTo(0, 0)
        }

        // Manually apply margin to Safari to fix unsupported spacing css
        if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
            document.querySelector('.table-of-contents').classList.add('safari-margin')
        }
    }, [])

    const currentPath = metadata?.path
    const inCortex = isCortexPath(currentPath)
    let rootMetadata = metadata
    while (rootMetadata?.metadataParent) {
        rootMetadata = rootMetadata.metadataParent
    }
    const rootPath = rootMetadata?.path

    // Reuse the completed tree during scroll and accordion renders. Copy nodes only when rebuilding.
    const displayNodes = useMemo(() => {
        const nodes = getNavigationNodes(
            data.allPageMetadata.edges.map((edge) => edge.node),
            currentPath,
        )
        /** This filters pages to the top level of navigation.
         * We also have a set of paths that we do not want to render, so we ignore EXCLUSION_PATHS */
        const headerNodes = nodes.filter((node) => {
            const path = node.path
            return (
                // if you have a path that is at the root, when you split by '/' you will get ['', path, '']
                path.split('/').length === TOP_LEVEL_NAV_SPLIT_LENGTH && EXCLUSION_PATHS.indexOf(path) < 0
            )
        })

        const findChildren = (node, allNodes, parent) => {
            if (!node || !node.path || !allNodes || (node.metadataChildren && node.parent)) return

            if (!node.metadataChildren) {
                const children = allNodes.filter((child) => {
                    if (!child.path.endsWith('/')) {
                        child.path += '/'
                    }
                    return (
                        child?.path &&
                        child.path !== node.path &&
                        child.path.startsWith(node.path) &&
                        child.path.split('/').filter((p) => p.length > 0).length ===
                            node.path.split('/').filter((p) => p.length > 0).length + 1
                    )
                })
                node.metadataChildren = children.sort((a, b) => {
                    if (a.order > b.order) {
                        return 1
                    }
                    if (a.order < b.order) {
                        return -1
                    }
                    return 0
                })
                node.parent = parent
            }

            node.metadataChildren.forEach((child) => {
                findChildren(child, allNodes, node)
            })
        }

        const buildRootNodes = (allNodes) => {
            const root = allNodes.find((n) => n.path === rootPath)
            // A directly linked hidden page still shows the public navigation.
            if (!root) return headerNodes

            // Redirect root nodes so we don't get pulled away from the markdown view
            const nodes = [root, ...headerNodes.filter((node) => node.path !== root.path)]
                .sort((a, b) => {
                    if (a.path < b.path) {
                        return -1
                    }
                    if (a.path > b.path) {
                        return 1
                    }
                    return 0
                })
                .map((node) => {
                    const redirect = REDIRECT_MAP.get(node.path)
                    return {
                        ...node,
                        redirect,
                    }
                })
            return nodes
        }

        const rootNodes = buildRootNodes(nodes)
        const root = {
            title: 'root',
            path: '/',
            metadataChildren: rootNodes,
        }
        findChildren(root, nodes, undefined)

        return inCortex ? rootNodes[0]?.metadataChildren || [] : rootNodes
    }, [data, currentPath, rootPath, inCortex])
    const { expandedPaths, toggleExpanded } = useLeftNavAccordion(metadata, displayNodes)

    return (
        <div className='table-of-contents'>
            {inCortex && <Link to='/cortex/'>Cortex home</Link>}
            <div className='dev-header'>
                {displayNodes.map((node) => {
                    return (
                        <NavigationNode
                            key={node.path}
                            node={node}
                            depth={inCortex ? 1 : 0}
                            metadata={metadata}
                            expandedPaths={expandedPaths}
                            onToggleExpanded={toggleExpanded}
                        />
                    )
                })}
            </div>
        </div>
    )
}

TableOfContents.propTypes = {
    metadata: routePropTypes.pageMetadata.isRequired,
}

export default TableOfContents

// Supports up to 5 nested parents
export const query = graphql`
      fragment NavMetadata on PageMetadata {
        title
        path
        hideFromNav
        sourceCode
        metadataParent {
            title
            path
            sourceCode
            hasVersionPicker
            metadataChildren {
                title
                path
                order
                label
            }
            metadataParent {
                title
                path
                metadataChildren {
                    title
                    path
                    order
                    label
                }
                metadataParent {
                    title
                    path
                    metadataChildren {
                        title
                        path
                        order
                        label
                    }
                    metadataParent {
                        title
                        path
                        metadataChildren {
                            title
                            path
                            order
                            label
                        }
                        metadataParent {
                            title
                            path
                            metadataChildren {
                                title
                                path
                                order
                                label
                            }
                        }
                    }
                }
            }
        }
    }
`
